// ATTACHED FILES, one panel for each of the three subjects (spec 09 §4.2's
// E2-2a2 annotation on the customer 360's Documents tab, §4.4's on a saved
// quote, §4.7's on the order detail; §2.29, §5's E2-2 rows 10 to 13, §7
// narrowing 19; D-323 rulings 8 to 12, D-335 rulings 1 and 2).
//
// WHAT IT LISTS. The subject's own attachments, newest first, twenty a page
// with Show more, each with its category chip, its title (or "Untitled" and
// its type), the type, the size, who attached it and when, Download, and
// Detach for a writer. A customer lists its own files, never those of its
// quotes and orders, which their own panels list (the list route's rule).
// Show detached files reads the list again with `include_detached=true`, and
// a detached row says so and still downloads, because detaching keeps the
// file (§2.29).
//
// THE CATEGORY FENCE IS THE SERVER'S (§7 narrowing 19, D-335 ruling 1). A
// reader without `sales.read_value` is never served a `commercial` row, and
// the list says how many it withheld (`withheld_count`, over the whole subject
// and never one page), so a planner reads "Files not shown to your role: 1"
// rather than a list that looks complete. The panel adds no fence of its own.
//
// WHO WRITES. The caller decides `canWrite` from the subject's own write code
// (`canAttachTo`, hooks/permissions.ts) and the subject's state, so the same
// panel offers Upload and Detach to a quote writer on the composer and to
// nobody on a qc reader's order. A writer the subject turns away for a stated
// reason (an archived customer) reads `writeClosedNote` in place of Upload.
// Detach asks first, in a dialog that says the file stays on record.
//
// AFTER EVERY WRITE THE LIST IS READ AGAIN (hooks/attachment-queries.ts): the
// attach and the detach store their answers without `title`, so a replayed
// answer is never painted.
//
// STATES (§4.2). Three skeleton rows while the first page loads; a failed
// read with its request id and Try again; a read held offline says so in one
// line; an empty list reads "No files attached yet", with Upload above it for
// a writer. Every download refusal is 404 `ATTACHMENT_NOT_FOUND` and reads
// that the file is no longer one the reader can open, and the list is read
// again. On a phone the table's rows become cards (`DataTable`'s collapse).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { CheckboxField } from "../../../../../components/field";
import { DataTable, type DataTableColumn } from "../../../../../components/data-table";
import { Modal } from "../../../../../components/modal";
import { ErrorState, Skeleton } from "../../../../../components/states";
import { StatusChip } from "../../../../../components/status-chip";
import { formatTimestamp } from "../../../../../lib/datetime";
import { requestIdOf } from "../../../../../lib/request";
import { useTenantTimezone } from "../../../../../lib/role-codes";
import { MutationFailureNote } from "../../mutation-failure-note";
import { useReadsHeldOffline } from "../../lib/reads";
import {
  useAttachmentDownload,
  useAttachments,
  useDetachAttachment,
  type AttachmentSubject,
  type AttachmentUploadWire,
  type SalesAttachment,
} from "../../hooks/attachment-queries";
import { attachmentRefusalKey, formatSize, typeLabel } from "./attachment-model";
import { UploadForm } from "./upload-form";
import "./attachments.css";
import { useLoadedPager } from "../../../../../lib/cursor-pages";
import { bookPager } from "../../hooks/book-pages";

export interface AttachmentsPanelProps {
  readonly subject: AttachmentSubject;
  /** Upload and Detach for this reader on this subject. */
  readonly canWrite: boolean;
  /** Shown to a writer in place of Upload when the subject takes no new file
   * for a stated reason (an archived customer). */
  readonly writeClosedNote?: string | undefined;
  /** The merged winner's name, for the merged refusal (customer subjects). */
  readonly mergedWinnerName?: string | undefined;
  /** Span the whole grid (the 360's Documents tab and the order detail). */
  readonly wide?: boolean;
  /** The upload calls, injected by tests; the real ones otherwise. */
  readonly wire?: AttachmentUploadWire | undefined;
}

export function AttachmentsPanel({
  subject,
  canWrite,
  writeClosedNote,
  mergedWinnerName,
  wide = false,
  wire,
}: AttachmentsPanelProps) {
  const { t, i18n } = useTranslation();
  const timeZone = useTenantTimezone();
  const offline = useReadsHeldOffline();
  const [showDetached, setShowDetached] = useState(false);
  const [confirming, setConfirming] = useState<SalesAttachment | null>(null);
  const list = useAttachments(subject, showDetached);
  const detach = useDetachAttachment(subject);
  const download = useAttachmentDownload(subject);

  // One page at a time (D-560): Next reads the following page when it is not
  // loaded yet; showing detached files starts again at page 1.
  const filePages = useLoadedPager(list, showDetached ? "all" : "attached");
  const items: readonly SalesAttachment[] = filePages.view.items;
  const withheld = list.data?.pages[0]?.withheld_count ?? 0;

  const columns: DataTableColumn<SalesAttachment>[] = [
    {
      key: "title",
      header: t("sales.attachments.col.title"),
      id: true,
      render: (row) =>
        row.title !== null ? (
          <span className="sales-attach-title" data-testid="sales-attachment-title">
            {row.title}
          </span>
        ) : (
          <span className="sales-attach-title" data-testid="sales-attachment-title">
            {t("sales.attachments.untitled", { type: typeLabel(row.content_type) })}
          </span>
        ),
    },
    {
      key: "category",
      header: t("sales.attachments.col.category"),
      render: (row) => (
        <span className="sales-attach-chips">
          <StatusChip
            variant={row.category === "commercial" ? "accent" : "neutral"}
            dot={false}
            data-testid="sales-attachment-category"
            data-category={row.category}
          >
            {t(`sales.attachments.category.${row.category}`)}
          </StatusChip>
          {row.status === "detached" ? (
            <StatusChip variant="warn" dot={false} data-testid="sales-attachment-detached">
              {t("sales.attachments.detachedChip")}
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    {
      key: "content_type",
      header: t("sales.attachments.col.type"),
      render: (row) => <span className="num">{typeLabel(row.content_type)}</span>,
    },
    {
      key: "byte_size",
      header: t("sales.attachments.col.size"),
      numeric: true,
      render: (row) => <span className="num">{formatSize(row.byte_size, i18n.language)}</span>,
    },
    {
      key: "attached_at",
      header: t("sales.attachments.col.attached"),
      age: true,
      render: (row) => {
        const date = formatTimestamp(row.attached_at, i18n.language, timeZone);
        return (
          <span data-testid="sales-attachment-meta">
            {row.attached_by_name !== null
              ? t("sales.attachments.byOn", { name: row.attached_by_name, date })
              : t("sales.attachments.on", { date })}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: t("sales.attachments.col.actions"),
      actions: true,
      render: (row) => (
        <span className="sales-attach-acts">
          <Button
            size="sm"
            data-testid="sales-attachment-download"
            disabled={offline || (download.isPending && download.variables === row.id)}
            busy={download.isPending && download.variables === row.id}
            busyLabel={t("sales.attachments.downloading")}
            onClick={() => {
              detach.reset();
              download.mutate(row.id);
            }}
          >
            {t("sales.attachments.download")}
          </Button>
          {canWrite && row.status === "active" ? (
            <Button
              size="sm"
              variant="danger"
              data-testid="sales-attachment-detach"
              disabled={offline || detach.isPending}
              onClick={() => {
                detach.reset();
                download.reset();
                setConfirming(row);
              }}
            >
              {t("sales.attachments.detach")}
            </Button>
          ) : null}
        </span>
      ),
    },
  ];

  const body = (() => {
    if (list.data === undefined) {
      if (list.error !== null) {
        return (
          <div data-testid="sales-attachments-error">
            <ErrorState
              title={t("sales.attachments.error")}
              retryLabel={t("sales.common.retry")}
              requestIdLabel={t("sales.common.requestId")}
              requestId={requestIdOf(list.error)}
              onRetry={() => {
                void list.refetch();
              }}
            />
          </div>
        );
      }
      if (list.fetchStatus === "paused") {
        return (
          <p className="sales-hint" role="status" data-testid="sales-attachments-offline">
            {t("sales.attachments.readOffline")}
          </p>
        );
      }
      return (
        <div className="sales-attach-skeleton" data-testid="sales-attachments-loading">
          <p className="visually-hidden" role="status">
            {t("sales.attachments.loading")}
          </p>
          {[0, 1, 2].map((row) => (
            <div key={row} className="sales-attach-skeleton-row" aria-hidden="true">
              <Skeleton height="1rem" />
              <Skeleton width="40%" height="0.8rem" />
            </div>
          ))}
        </div>
      );
    }
    return (
      <>
        {withheld > 0 ? (
          <p className="sales-hint" data-testid="sales-attachments-withheld" data-count={withheld}>
            {t("sales.attachments.withheld", { count: withheld })}
          </p>
        ) : null}
        <DataTable
          data-testid="sales-attachments-table"
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          rowTestId="sales-attachment-row"
          rowProps={(row) => ({
            "data-attachment-id": row.id,
            "data-file-id": row.file_id,
            "data-category": row.category,
            "data-status": row.status,
          })}
          caption={t("sales.attachments.title")}
          fit
          empty={
            <p className="sales-hint" data-testid="sales-attachments-empty">
              {showDetached ? t("sales.attachments.emptyAll") : t("sales.attachments.empty")}
            </p>
          }
          pager={bookPager(
            t,
            i18n.language,
            { ...filePages.view, hasNext: filePages.view.hasNext && !offline },
            {
              turning: filePages.turning,
              onNext: filePages.next,
              onPrev: filePages.prev,
            },
          )}
        />
        {list.isFetchNextPageError ? (
          <p className="sales-error-note" role="alert" data-testid="sales-attachments-more-failed">
            {t("sales.attachments.moreFailed")}
          </p>
        ) : null}
      </>
    );
  })();

  const downloadRefusal = download.isError ? attachmentRefusalKey(download.error) : undefined;
  const detachRefusal = detach.isError ? attachmentRefusalKey(detach.error) : undefined;

  return (
    <section
      className={
        wide
          ? "ds-card sales-360-card sales-360-wide sales-attach"
          : "ds-card sales-360-card sales-attach"
      }
      data-testid="sales-attachments"
      data-subject-kind={subject.kind}
      data-subject-id={subject.id}
    >
      <header className="sales-attach-head">
        <h3>{t("sales.attachments.title")}</h3>
        <CheckboxField
          label={t("sales.attachments.showDetached")}
          checked={showDetached}
          data-testid="sales-attachments-show-detached"
          onChange={(checked) => {
            setShowDetached(checked);
          }}
        />
      </header>

      {canWrite && writeClosedNote === undefined ? (
        <UploadForm subject={subject} mergedWinnerName={mergedWinnerName} wire={wire} />
      ) : null}
      {canWrite && writeClosedNote !== undefined ? (
        <p className="sales-hint" data-testid="sales-attach-closed">
          {writeClosedNote}
        </p>
      ) : null}

      {download.isError ? (
        <MutationFailureNote
          data-testid="sales-attachment-download-failed"
          text={
            downloadRefusal !== undefined
              ? t(downloadRefusal)
              : t("sales.attachments.downloadFailed")
          }
          error={download.error}
        />
      ) : null}
      {detach.isError ? (
        <MutationFailureNote
          data-testid="sales-attachment-detach-failed"
          text={
            detachRefusal !== undefined ? t(detachRefusal) : t("sales.attachments.detachFailed")
          }
          error={detach.error}
        />
      ) : null}

      {body}

      {confirming !== null ? (
        <Modal
          label={t("sales.attachments.detachTitle")}
          heading={t("sales.attachments.detachTitle")}
          closeLabel={t("sales.common.close")}
          pending={detach.isPending}
          testId="sales-attachment-detach-confirm"
          onClose={() => {
            setConfirming(null);
          }}
        >
          <p className="sales-attach-confirm-text">
            {t("sales.attachments.detachBody", {
              name:
                confirming.title ??
                t("sales.attachments.untitled", { type: typeLabel(confirming.content_type) }),
            })}
          </p>
          <div className="sales-toolbar">
            <Button
              size="sm"
              variant="danger"
              data-testid="sales-attachment-detach-yes"
              disabled={offline}
              busy={detach.isPending}
              busyLabel={t("sales.attachments.detaching")}
              onClick={() => {
                detach.mutate(confirming.id, {
                  onSettled: () => {
                    setConfirming(null);
                  },
                });
              }}
            >
              {t("sales.attachments.detachYes")}
            </Button>
            <Button
              size="sm"
              data-testid="sales-attachment-detach-no"
              disabled={detach.isPending}
              onClick={() => {
                setConfirming(null);
              }}
            >
              {t("sales.common.cancel")}
            </Button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
